import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, FormattedDate } from 'react-intl';
import getFirstDateOfWeek from 'date-fns/start_of_week';
import addDays from 'date-fns/add_days';
import Truncate from 'react-truncate';

// Components
import { Container, Row, Col } from 'reactstrap';
import Alert from 'components/atoms/Alert';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowHeader from 'components/molecules/AppointmentFlowHeader';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter';
import Calendar from 'components/organisms/Calendar';
import Panel from 'components/atoms/Panel';
import TimeSelector from 'components/molecules/TimeSelector';
import AdvisorTransportationPanel from 'components/molecules/AdvisorTransportationPanel';
import AppointmentError from 'components/api/components/AppointmentNotAvailable/AppointmentError';
import FatalError from 'components/api/components/FatalError/FatalError';
import { resetScrollPosition } from 'utils/DOM';
// CTA Components
import CTAFlowHeader from 'components/molecules/CTAFlowHeader';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Constants
import {
  DROPOFF_ROUTE,
  REVIEW_ROUTE,
  PICKUP_ROUTE,
  SERVICES_ROUTE,
  INDIVIDUAL_SERVICES_ROUTE,
  VEHICLE_ROUTE
} from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';
import { TRANSPORTATION } from 'constants/transportation';
import { APP_FLOW } from 'constants/appFlow';
import { labelsStepIndicator } from 'constants/CTAConfig';

// Utils
import { toISODate, toISOTime, getCTAbackButtonLabel } from 'utils/string';
import { formatTime } from 'utils/date';

import './AppointmentPage.scss';
import UnavailableBanner from 'components/atoms/UnavailableBanner';

const { FORWARD } = NAVIGATION;
const { VALET, VALETLOANER } = TRANSPORTATION;
const { QUICK_APPT } = APP_FLOW;
const ns = 'appointment-page';

class AppointmentPage extends Component {
  constructor(props) {
    super(props);
    this.handleShow = this.handleShow.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.getCalendarInfo = this.getCalendarInfo.bind(this);
    this.onAdvisorTransportationSelect = this.onAdvisorTransportationSelect.bind(
      this
    );
    this.state = {
      showServicesList: false,
      scrolldown: false,
      selectedDate: props.startTime || props.firstAvailableTimeSlot
    };
    (this: any).noAdvisorsErrorPopup = React.createRef();
  }

  componentDidMount() {
    const { noAdvisorsError, previousPath } = this.props;
    const isAdvisorError =
      noAdvisorsError &&
      (SERVICES_ROUTE === previousPath ||
        INDIVIDUAL_SERVICES_ROUTE === previousPath);
    if (isAdvisorError) {
      (this: any).noAdvisorsErrorPopup.current.setShowModal(true);
    }

    resetScrollPosition(0);
    this.getCalendarInfo();
  }

  componentDidUpdate() {
    const {
      gtmFirstAvailableTimeslot,
      firstAvailableTimeSlot,
      setGtmFirstAvailableTimeslot
    } = this.props;
    if (!gtmFirstAvailableTimeslot) {
      setGtmFirstAvailableTimeslot(firstAvailableTimeSlot);
    }
  }

  checkSelectedDateAvailability = () => {
    const {
      setNoLongerAvailableDate,
      lastTimeslotSelected,
      filteredAvailableDays
    } = this.props;
    if (lastTimeslotSelected) {
      const selectedDay = filteredAvailableDays.find(
        ({ calendarDate }) => calendarDate === toISODate(lastTimeslotSelected)
      );
      if (
        selectedDay &&
        selectedDay.calendarDate === toISODate(lastTimeslotSelected) &&
        selectedDay.timeslots
      ) {
        const timeslot = selectedDay.timeslots.find(
          ({ time }) => time === toISOTime(lastTimeslotSelected)
        );
        if (!timeslot) {
          setNoLongerAvailableDate(true);
        }
      } else {
        setNoLongerAvailableDate(true);
      }
    }
  };

  async getCalendarInfo() {
    const {
      startTime: startTimeFromState,
      getAvailableTimesByDates,
      getFirstAvailability,
      getApptAdvisorsAndTranspOptions,
      lastTimeslotSelected,
      selectedTransportation,
      externalProvider,
      previousPath,
      setFirstAvailableTimeSlot,
      availableDays,
      cleanDayTimeError
    } = this.props;
    cleanDayTimeError();
    const startTime = startTimeFromState || lastTimeslotSelected;
    const isValet =
      selectedTransportation === VALET ||
      selectedTransportation === VALETLOANER;
    await getApptAdvisorsAndTranspOptions();

    // Verify if availability was already requested for external valets
    if (!isValet || !externalProvider || previousPath !== PICKUP_ROUTE) {
      if (startTime) {
        const date = getFirstDateOfWeek(startTime);
        const endDate = addDays(date, 13);
        await getAvailableTimesByDates(date, endDate);
      } else {
        await getFirstAvailability();
      }
    } else {
      // This is a way to call update in Calendar component
      setFirstAvailableTimeSlot(availableDays);
    }
    this.checkSelectedDateAvailability();
  }

  handleShow() {
    this.setState({ showServicesList: true });
  }

  getDateTimeFooter = startTime => {
    // prettier-ignore
    const {
      intl,
      intl: { formatDate }
    } = this.props;
    const dateLabel = formatDate(startTime, {
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
    const timeLabel = formatTime((startTime || '').toString(), intl);
    const ariaLabelDateTime = dateLabel + ' ' + timeLabel;
    if (startTime) {
      return (
        <div
          className="date-time"
          tabIndex={0}
          aria-label={intl.formatMessage(
            {
              id: 'c8.aria.appointment.selected',
              defaultMessage: 'Appointment scheduled for {ariaLabelDateTime}'
            },
            {
              ariaLabelDateTime
            }
          )}
        >
          <div className="date">
            {formatDate(startTime, {
              month: 'short',
              day: 'numeric',
              weekday: 'short'
            })}
          </div>
          <div className="time">{formatTime(startTime, intl, false, true)}</div>
        </div>
      );
    }
  };

  onDateChange(selectedDate, scrollDown) {
    this.setState({ selectedDate, scrollDown });
  }

  async onAdvisorTransportationSelect() {
    const {
      startTime,
      firstAvailableTimeSlot,
      setAppointmentSelectedDay,
      getAvailableTimesByDates
    } = this.props;
    const date = getFirstDateOfWeek(
      startTime || firstAvailableTimeSlot || new Date()
    );
    const endDate = addDays(date, 13);
    await getAvailableTimesByDates(date, endDate);
    setAppointmentSelectedDay(null);
    this.checkSelectedDateAvailability();
  }

  render() {
    const {
      startTime,
      firstAvailableTimeSlot,
      goTo,
      gtmUserEventDefaultDateSelected,
      promotion,
      selectedTransportation,
      noLongerAvailableDate,
      setNoLongerAvailableDate,
      setLastTimeslotSelected,
      lastTimeslotSelected,
      setStartTime,
      intl,
      appFlow,
      ctaBanner,
      gtmUserEvent,
      vehicle,
      showProgressBar,
      hideBottomCTA,
      ctaBackButton,
      isValetOrLoanerIntegration,
      selectedTimeslotInPromoRange
    } = this.props;
    const { selectedDate, scrollDown } = this.state;
    const classProps = classNames(ns, {
      [`${ns}--footer-bottom-up`]: startTime,
      [`${ns}--footer-hidden`]: !startTime
    });
    const backButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.back.to.transportation',
      defaultMessage: 'Back to transportation'
    });
    const nextButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.continue.to.review',
      defaultMessage: 'Continue to review'
    });
    const { make, year, model } = vehicle;
    const isValet =
      selectedTransportation === VALET ||
      selectedTransportation === VALETLOANER;
    let routeTo;
    let headerLabel;

    if (appFlow === QUICK_APPT) {
      const formattedLabel = intl.formatMessage({
        id: 'c8.vehicle.page.other'
      });
      routeTo = VEHICLE_ROUTE;
      headerLabel = `${make} · ${year} · ${model}`;
      headerLabel = headerLabel.replace(/OTHER/g, formattedLabel);
    } else {
      routeTo = isValet ? PICKUP_ROUTE : DROPOFF_ROUTE;
      headerLabel = intl.formatMessage({ id: 'c8.header.transportation' });
    }
    const ctaBackButtonLabel =
      appFlow === QUICK_APPT ? 'c8.header.vehicle' : 'c8.header.transportation';
    const ctaLabel = getCTAbackButtonLabel(
      ctaBackButton,
      headerLabel,
      ctaBackButtonLabel,
      intl
    );
    return (
      <React.Fragment>
        <FatalError
          key="fatalError"
          ref={(this: any).noAdvisorsErrorPopup}
          noAdvisorsError={true}
        />
        <AppointmentFlowWizard
          className={classProps}
          hideFooter={Boolean(hideBottomCTA)}
          header={() => {
            return ctaBanner !== null ? (
              <CTAFlowHeader
                label={ctaLabel}
                hideServices={false}
                routeTo={routeTo}
                backButtonAriaLabel={backButtonAriaLabel}
                nextButtonAriaLabel={nextButtonAriaLabel}
                ctaButtonDisabled={!startTime}
                ctaBanner={ctaBanner}
                onClickCtaButton={() => {
                  gtmUserEventDefaultDateSelected();
                  gtmUserEvent(
                    'NEXT button',
                    'Clicked',
                    `Go to next page through CTA`,
                    `GTM_CURRENT_PAGE > ${REVIEW_ROUTE} page`
                  );
                  goTo(REVIEW_ROUTE, FORWARD);
                }}
                typeButton={'cta-next'}
              />
            ) : (
              <AppointmentFlowHeader
                onCartClick={this.handleShow}
                label={headerLabel}
                routeTo={routeTo}
                backButtonAriaLabel={backButtonAriaLabel}
              />
            );
          }}
          footer={() => {
            return !hideBottomCTA ? (
              <AppointmentFlowFooter
                buttonDisabled={!startTime}
                buttonText={intl.formatMessage({ id: 'c8.footer.review' })}
                onClick={() => {
                  gtmUserEventDefaultDateSelected();
                  goTo(REVIEW_ROUTE, FORWARD);
                }}
                extraCol={() => (
                  <Col sm={5} xs={5} className="date-footer-container">
                    {this.getDateTimeFooter(startTime)}
                  </Col>
                )}
                ariaLabel={nextButtonAriaLabel}
              />
            ) : null;
          }}
          body={() => (
            <div>
              {showProgressBar && (
                <CtaStepIndicator
                  htmlId="VehiclePageStepIndicator"
                  labels={labelsStepIndicator(intl)}
                  activeStep={4}
                />
              )}
              {firstAvailableTimeSlot && (
                <Alert color="primary" animation="top-down">
                  <FormattedMessage
                    id="c8.alert.first.available.day"
                    values={{
                      firstAvailableTimeSlot: (
                        <span className={'first-available-date'}>
                          <FormattedDate
                            value={firstAvailableTimeSlot}
                            month={'short'}
                            day={'numeric'}
                          />
                        </span>
                      )
                    }}
                  />
                </Alert>
              )}
              {promotion &&
                lastTimeslotSelected &&
                !selectedTimeslotInPromoRange && (
                  <Alert
                    color="warning"
                    animation="top-down"
                    key={lastTimeslotSelected}
                  >
                    <FormattedMessage
                      id="c8.alert.no.timeslot.in.promo.range"
                      defaultMessage="Promotion is not available for the selected time. Please choose an eligible time to apply promotion."
                    />
                  </Alert>
                )}
              {noLongerAvailableDate && (
                <AppointmentError
                  ref={this.errorBooking}
                  isOpen={noLongerAvailableDate}
                  onClose={() => {
                    setNoLongerAvailableDate(false);
                    setLastTimeslotSelected(null);
                    setStartTime(null);
                  }}
                  date={lastTimeslotSelected}
                />
              )}
              <Container>
                <Row className={`${ns}__title`}>
                  <Col xs={12} sm={6}>
                    <h1 className={`${ns}__title--aria-main-title`}>
                      {selectedTransportation === VALET ||
                      selectedTransportation === VALETLOANER
                        ? intl.formatMessage({
                            id: 'c8.appointment.page.pick.up.time'
                          })
                        : intl.formatMessage({
                            id: 'c8.appointment.page.title.appointment.time'
                          })}
                    </h1>
                  </Col>
                </Row>
                <UnavailableBanner />
                {!isValetOrLoanerIntegration && (
                  <AdvisorTransportationPanel
                    onSelect={this.onAdvisorTransportationSelect}
                  />
                )}
                {promotion && (
                  <div className={`${ns}__label`}>
                    <span className={`${ns}__dot`} />
                    <span className={`${ns}__label--margin`}>
                      <Truncate lines={2} ellipsis="…">
                        {intl.formatMessage(
                          { id: 'c8.appointment.page.details.promotion.label' },
                          { promoDescription: promotion.promotionDescription }
                        )}
                      </Truncate>
                    </span>
                  </div>
                )}
                <Panel>
                  <Row className="no-gutters">
                    <Col xs={12} md={10} className={'mx-auto'}>
                      <Calendar
                        onSelect={this.onDateChange}
                        monthDate={startTime || firstAvailableTimeSlot}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      {selectedDate && (
                        <div className={`${ns}__label`}>
                          <FormattedMessage
                            id="c8.appointment.details.showing.times"
                            values={{
                              selectedDate: (
                                <span className={`${ns}__label--primary`}>
                                  <FormattedDate
                                    value={selectedDate}
                                    month={'long'}
                                    day={'numeric'}
                                  />
                                </span>
                              )
                            }}
                          />
                        </div>
                      )}
                      <TimeSelector
                        selectedDate={selectedDate}
                        scrollDown={scrollDown}
                      />
                    </Col>
                  </Row>
                </Panel>
              </Container>
            </div>
          )}
        />
      </React.Fragment>
    );
  }
}

AppointmentPage.propTypes = {
  currentPath: PropTypes.string,
  filteredAvailableDays: PropTypes.array,
  firstAvailableTimeSlot: PropTypes.instanceOf(Date),
  getApptAdvisorsAndTranspOptions: PropTypes.func.isRequired,
  getAvailableTimesByDates: PropTypes.func.isRequired,
  getFirstAvailability: PropTypes.func.isRequired,
  goTo: PropTypes.func.isRequired,
  gtmFirstAvailableTimeslot: PropTypes.instanceOf(Date),
  gtmUserEventDefaultDateSelected: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  lastTimeslotSelected: PropTypes.instanceOf(Date),
  noAdvisorsError: PropTypes.bool,
  noLongerAvailableDate: PropTypes.bool.isRequired,
  previousPath: PropTypes.string,
  promotion: PropTypes.object,
  selectedTransportation: PropTypes.string,
  setAppointmentSelectedDay: PropTypes.func.isRequired,
  setGtmFirstAvailableTimeslot: PropTypes.func.isRequired,
  setLastTimeslotSelected: PropTypes.func.isRequired,
  setNoLongerAvailableDate: PropTypes.func.isRequired,
  setStartTime: PropTypes.func.isRequired,
  startTime: PropTypes.instanceOf(Date),
  appFlow: PropTypes.string,
  ctaBanner: PropTypes.object,
  gtmUserEvent: PropTypes.func,
  vehicle: PropTypes.object,
  showProgressBar: PropTypes.bool,
  hideBottomCTA: PropTypes.bool,
  ctaBackButton: PropTypes.string,
  externalProvider: PropTypes.string,
  isValetOrLoanerIntegration: PropTypes.string.isRequired,
  selectedTimeslotInPromoRange: PropTypes.bool
};

export default AppointmentPage;



// WEBPACK FOOTER //
// ./src/components/pages/AppointmentPage/AppointmentPage.js